import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";
import BreadcrumbGroup from "@cloudscape-design/components/breadcrumb-group";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Table from "@cloudscape-design/components/table";
import Spinner from "@cloudscape-design/components/spinner";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import { lambdaFunctionUrl } from "./awsConsoleUrls";

interface LambdaDetail {
  functionName: string;
  functionArn: string;
  runtime: string;
  handler: string;
  description: string;
  codeSize: number;
  lastModified: string;
  memorySize: number;
  timeout: number;
  state: string;
  architecture: string;
  environment: { key: string; value: string }[];
  tags: { key: string; value: string }[];
}

export default function LiveLambdaDetailPage() {
  const { functionName } = useParams<{ functionName: string }>();
  const [searchParams] = useSearchParams();
  const account = searchParams.get("account");
  const region = searchParams.get("region") || getDefaultRegion();

  const [fn, setFn] = useState<LambdaDetail | null>(null);
  useDocumentTitle(fn?.functionName || functionName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const token = getAuthToken();
        if (!token) throw new Error("No auth token");

        const accountsRes = await apiFetch(`/accounts`);
        const { data } = await accountsRes.json();
        const acc = data.find((a: any) => a.documentId === account);
        if (!acc) throw new Error("Account not found");

        const creds = getAwsCreds(acc.Alias);
        if (!creds) throw new Error(`No AWS credentials for account "${acc.Alias}"`);

        const res = await apiFetch(
          `/inventory/live/lambda/${functionName}?region=${region}`,
          {
            headers: {
              "X-AWS-Access-Key-Id": creds.accessKeyId,
              "X-AWS-Secret-Access-Key": creds.secretAccessKey,
              "X-AWS-Session-Token": creds.sessionToken,
            },
          }
        );
        if (!res.ok) throw new Error(`Failed to fetch function: ${res.statusText}`);
        setFn(await res.json());
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [functionName, account, region]);

  if (loading) return <Spinner size="large" />;
  if (error) return <StatusIndicator type="error">{error}</StatusIndicator>;
  if (!fn) return null;

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "Lambda", href: "/live-inventory/lambda" },
          { text: functionName || "", href: "" },
        ]}
      />

      <Container header={<Header
        actions={
          fn.functionName && region ? (
            <Button
              iconName="external"
              href={lambdaFunctionUrl(fn.functionName, region)}
              target="_blank"
            >
              Open in AWS Console
            </Button>
          ) : undefined
        }
      >Function Details</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div><Box variant="awsui-key-label">Function Name</Box><div>{fn.functionName}</div></div>
          <div><Box variant="awsui-key-label">ARN</Box><div>{fn.functionArn}</div></div>
          <div><Box variant="awsui-key-label">Runtime</Box><div>{fn.runtime}</div></div>
          <div><Box variant="awsui-key-label">Handler</Box><div>{fn.handler}</div></div>
          <div><Box variant="awsui-key-label">Description</Box><div>{fn.description}</div></div>
          <div><Box variant="awsui-key-label">Memory Size</Box><div>{fn.memorySize} MB</div></div>
          <div><Box variant="awsui-key-label">Timeout</Box><div>{fn.timeout} s</div></div>
          <div><Box variant="awsui-key-label">Code Size</Box><div>{fn.codeSize} bytes</div></div>
          <div><Box variant="awsui-key-label">Last Modified</Box><div>{fn.lastModified}</div></div>
          <div><Box variant="awsui-key-label">Architecture</Box><div>{fn.architecture}</div></div>
          <div>
            <Box variant="awsui-key-label">State</Box>
            <StatusIndicator type={fn.state === "Active" ? "success" : "info"}>{fn.state}</StatusIndicator>
          </div>
        </ColumnLayout>
      </Container>

      <Table
        header={<Header>Environment Variables</Header>}
        columnDefinitions={[
          { id: "key", header: "Key", cell: (item: { key: string; value: string }) => item.key },
          { id: "value", header: "Value", cell: (item: { key: string; value: string }) => item.value },
        ]}
        items={fn.environment || []}
        empty="No environment variables."
      />

      <Table
        header={<Header>Tags</Header>}
        columnDefinitions={[
          { id: "key", header: "Key", cell: (item: { key: string; value: string }) => item.key },
          { id: "value", header: "Value", cell: (item: { key: string; value: string }) => item.value },
        ]}
        items={fn.tags || []}
        empty="No tags."
      />
    </SpaceBetween>
  );
}

